<template>
    <div style="height: 800px; width: 100%;">
        <line-chart id="research-primary" style="z-index:5" :chart-options="chartOptions" :chart-data="chartData" :plugins="plugins">Failed to load.</line-chart>
    </div>
</template>
<script>
    import { ref, inject, computed, onMounted } from "vue";
    import lineChart from "@/components/research/line-chart.vue";
    import functions from "@/components/research/functions.js";
    import { toFont } from 'chart.js/helpers';
    import externalTooltip from "@/components/research/externalTooltip.js";
    import { min } from "moment";

    export default {
        name: 'PrimaryChart',
        components: {
            lineChart
        },
        props: {
            startDate: {
                type: [Date, String, Object],
                default: () => new Date(new Date().getFullYear() - 1, 1, 1)
            },
            endDate: {
                type: [Date, String, Object],
                default: () => new Date()
            },
            customOptions: {
                type: Object,
                default: () => ({
                    avgRange: 12,
                    hideTooltip: false
                })
            },
            rawData: {
                type: Array,
                default: () => []
            },
            transactions: {
                type: Array,
                default: () => []
            },
            plugins: {
                type: Array,
                default: () => []
            }
        },
        setup(props, context) {
            const moment = inject("$moment");

            function getFirstNonNullIndex(...arrays) {
                const minLength = Math.min(...arrays.map(arr => arr.length));

                for (let i = 0; i < minLength; i++) {
                    const nonNullValues = arrays.map(arr => arr[i]);
                    if (nonNullValues.every(val => val !== null && val !== undefined)) {
                        return i;
                    }
                }
                return -1;
            }

            const tooltip_hidden = computed(() => props.customOptions?.hideTooltip === true);

            const data = computed(() => {
                if (!props.startDate || !props.endDate) return props.rawData;

                return props.rawData.filter(d => {
                    const date = moment(d["c_date"], "YYYY-MM-DD").toDate();
                    return props.startDate <= date && date <= props.endDate;
                });
            });

            const transactionPoints = computed(() => {
                const minSplitFactor = data.value.at(-1)?.b_split || 1;

                return props.transactions.map(t => {
                    const periodIndex = data.value.findIndex(d => d["c_period_id"] === t["period"]);
                    const size = t.size || 0;
                    const action = t.action.toLowerCase();

                    let color, borderColor;
                    const intensity = size === 0 ? 255 :
                        size === 1 ? 100 :
                            size === 2 ? 175 : 250;

                    if (action === "buy") {
                        borderColor = 'rgba(0,255,0,1)';
                        color = `rgba(0, ${intensity}, 0, 1)`;
                    } else if (action === "sell") {
                        borderColor = 'rgba(255,0,0,1)';
                        color = `rgba(${intensity}, 0, 0, 1)`;
                    }

                    if (size === 0) {
                        color = 'rgba(0, 0, 0, 0)';
                    }
                    borderColor = color;

                    return {
                        type: 'point',
                        yScaleID: 'y_right',
                        xValue: periodIndex,
                        radius: 6,
                        yValue: functions.price(data.value[periodIndex], minSplitFactor),
                        backgroundColor: color,
                        borderColor: borderColor,
                        borderWidth: 2,
                    };
                });
            });

            const chartOptions = computed(() => {
                // split factor = the end date data point in the filtered b_split field in bbpe look up by period_id
                //const minSplitFactor = Math.min(...data.value.map(d => d["price_1"]));
                const minSplitFactor = data.value.at(-1)?.b_split || 1;

                return {
                    z_index: 0,
                    name: "Top Chart",
                    responsive: true,
                    maintainAspectRatio: false,
                    animation: false,
                    backgroundColor: "rgba(0,0,0,0)",
                    scales: {
                        x: {
                            ticks: {
                                callback: function (val, index) {
                                    if (!val) return '';
                                    const parts = this.getLabelForValue(val).split('-');
                                    const d = new Date(parts[0], parts[1] - 1, parts[2]);
                                    return moment(d).format("YYYY");
                                }
                            }
                        },
                        y_left: {
                            afterUpdate: function (scale) {
                                const rightScale = scale.chart.scales.y_right;
                                scale.chart.scales.y_left.start = rightScale.start / 15;
                                scale.chart.scales.y_left.end = rightScale.end / 15;
                                scale.chart.scales.y_left.min = rightScale.min / 15;
                                scale.chart.scales.y_left.max = rightScale.max / 15;
                                scale.chart.scales.y_left.ticks = rightScale.ticks
                                    .filter(v => v.value % 15 === 0)
                                    .map(v => {
                                        v.value /= 15;
                                        v.label = v.value.toString();
                                        return v;
                                    });
                            },
                            afterFit: function (scaleInstance) {
                                scaleInstance.width = 50;
                            },
                            type: 'logarithmic',
                            display: true,
                            position: 'left',
                        },
                        y_right: {
                            type: 'logarithmic',
                            display: true,
                            position: 'right',
                            afterFit: function (scaleInstance) {
                                scaleInstance.width = 100;
                            },
                            min: 0,
                            suggestedMax: Math.max(...data.value.map(d => functions.price(d, minSplitFactor))) * 1.25,
                        }
                    },
                    interaction: {
                        mode: 'index',
                        intersect: false
                    },
                    plugins: {
                        tooltip: {
                            enabled: false,
                            position: 'nearest',
                            hideTooltip: props.customOptions?.hideTooltip === true,
                            external: externalTooltip
                        },
                        annotation: {
                            annotations: transactionPoints.value
                        }
                    },
                    layout: {
                        autoPadding: false
                    }
                };
            });

            const chartData = computed(() => {
                const minSplitFactor = data.value.at(-1)?.b_split || 1;
                const avgRange = Number.parseInt(props.customOptions.avgRange) || 12;
                const avgPriceSeries = functions.average_price_behind_series(data.value, avgRange, minSplitFactor);

                return {
                    labels: data.value.map(d => d["c_date"]),
                    datasets: [
                        {
                            label: "Price",
                            fill: false,
                            borderColor: "blue",
                            data: data.value.map(d => functions.price(d, minSplitFactor)),
                            yAxisID: 'y_right',
                            pointRadius: 1
                        },
                        {
                            label: "EPS",
                            fill: false,
                            borderColor: "magenta",
                            data: data.value.map(d => functions.trailing_earnings(d, minSplitFactor)),
                            yAxisID: 'y_left',
                            pointRadius: 1
                        },
                        {
                            label: "EPS Estimate",
                            fill: false,
                            borderColor: "purple",
                            data: data.value.map(d => functions.one_year_forward_earnings(d, minSplitFactor)),
                            yAxisID: 'y_left',
                            pointRadius: 1
                        },
                        {
                            label: "EPS 2 yr. Estimate",
                            fill: false,
                            borderColor: "violet",
                            data: data.value.map(d => functions.two_year_forward_earnings(d, minSplitFactor)),
                            yAxisID: 'y_left',
                            pointRadius: 1
                        },
                        {
                            label: "Dividend",
                            fill: false,
                            borderColor: "lightgreen",
                            data: data.value.map(d => functions.dividend(d, minSplitFactor)),
                            yAxisID: 'y_left',
                            pointRadius: 1
                        },
                        {
                            label: "Avg Price",
                            fill: false,
                            borderColor: "lightblue",
                            borderDash: [5, 5],
                            data: avgPriceSeries,
                            yAxisID: 'y_right',
                            pointRadius: 1
                        }
                    ]
                };
            });

            return {
                chartOptions,
                chartData,
                transactionPoints
            };
        }
    }
</script>
